<script setup lang="ts">
// The promo is a self-contained canvas "video" (public/promo/onboarding.html) — it
// draws and scores itself, so it's embedded as-is rather than a real video file.
// The iframe only mounts while the modal is open, so closing it stops playback.
const open = defineModel<boolean>({ default: false })

// the promo renders its English cut with ?lang=en; any other locale gets the original Arabic
const { locale } = useI18n()
const src = computed(() => `/promo/onboarding.html?embed${locale.value.startsWith('en') ? '&lang=en' : ''}`)

const localePath = useLocalePath()

function onMessage(e: MessageEvent) {
  if (e.origin !== window.location.origin) return
  if (e.data === 'bfm-tour-close') open.value = false
  // the onboarding end card's "request a demo" button
  if (e.data === 'bfm-tour-cta') {
    open.value = false
    navigateTo(localePath('/request-demo'))
  }
}

onMounted(() => window.addEventListener('message', onMessage))
onUnmounted(() => window.removeEventListener('message', onMessage))
</script>

<template>
  <UModal
    v-model="open"
    :ui="{
      // above the fixed site nav (z-[100])
      wrapper: 'relative z-[110]',
      // as wide as the screen allows while a 16:9 frame still fits in 85svh (85 × 16/9 ≈ 151)
      width: 'w-full sm:max-w-[min(92vw,151svh)]',
      // phones: full screen like a native video player; larger screens: centered card
      container: 'flex min-h-full items-center justify-center',
      padding: 'p-0 sm:p-4',
      margin: 'sm:my-8',
      background: 'bg-gray-900',
      rounded: 'rounded-none sm:rounded-2xl',
    }"
  >
    <div class="relative w-full overflow-hidden h-svh sm:h-auto sm:aspect-[16/9] sm:max-h-[85svh] sm:rounded-2xl">
      <iframe
        :src="src"
        :title="$t('page.home.hero_new.quick_tour')"
        allow="autoplay; fullscreen"
        allowfullscreen
        class="absolute inset-0 size-full border-0"
      />
      <button
        type="button"
        :aria-label="$t('page.home.hero_new.close_tour')"
        class="absolute top-[max(0.75rem,env(safe-area-inset-top))] end-3 z-10 flex items-center justify-center size-9 rounded-full bg-gray-900/60 text-white backdrop-blur transition-colors hover:bg-gray-900/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cheese-yellow"
        @click="open = false"
      >
        <UIcon name="i-approved-close" class="size-4" />
      </button>
    </div>
  </UModal>
</template>
